/**
 * NotificationPreferences - User settings for notification customization
 * Allows users to control alert types and delivery channels
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { 
  Bell, Mail, Smartphone, Monitor, TrendingDown, Search, Scale, 
  Home, Megaphone, Moon, Clock, Save, Loader2, CheckCircle,
  ArrowLeft, RefreshCw, AlertCircle, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alert type configurations
const ALERT_TYPES = [
  {
    key: 'price_alerts',
    title: 'Price Drop Alerts',
    description: 'Get notified when prices drop for your tracked properties',
    icon: TrendingDown,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    recommended: true
  },
  {
    key: 'saved_search_alerts',
    title: 'Saved Search Matches',
    description: 'New listings matching your saved search criteria',
    icon: Search,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    recommended: true
  },
  {
    key: 'comparison_updates',
    title: 'Comparison Updates',
    description: 'Changes to properties in your saved comparisons',
    icon: Scale,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    recommended: false
  },
  {
    key: 'new_listings',
    title: 'New Listings',
    description: 'Daily updates on new properties in your areas of interest',
    icon: Home,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    recommended: false
  },
  {
    key: 'promotions',
    title: 'Promotions & Tips',
    description: 'Special offers, market insights, and property buying tips',
    icon: Megaphone,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    recommended: false
  }
];

const FREQUENCY_OPTIONS = [
  { value: 'instant', label: 'Instant', description: 'Get notified immediately' },
  { value: 'daily_digest', label: 'Daily Digest', description: 'Once per day' },
  { value: 'weekly_digest', label: 'Weekly Digest', description: 'Once per week' }
];

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

// Channel toggle component
const ChannelToggle = ({ icon: Icon, label, checked, onChange, disabled }) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
    checked ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
  } ${disabled ? 'opacity-50' : ''}`}>
    <Icon className={`w-4 h-4 ${checked ? 'text-blue-600' : 'text-gray-400'}`} />
    <span className={`text-sm flex-1 ${checked ? 'text-gray-900' : 'text-gray-500'}`}>{label}</span>
    <Switch 
      checked={checked} 
      onCheckedChange={onChange} 
      disabled={disabled}
      className="data-[state=checked]:bg-blue-600"
    />
  </div>
);

// Alert type card component
const AlertTypeCard = ({ config, settings, onUpdate, loading }) => {
  const Icon = config.icon;
  const isEnabled = settings?.enabled ?? true;
  const channels = settings?.channels || { push: true, email: true, in_app: true };
  const frequency = settings?.frequency || 'instant';

  return (
    <Card className={`transition-all ${!isEnabled ? 'opacity-60' : ''}`} data-testid={`alert-type-${config.key}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${config.bgColor}`}>
              <Icon className={`w-5 h-5 ${config.color}`} />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {config.title}
                {config.recommended && (
                  <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-700">
                    Recommended
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-sm">{config.description}</CardDescription>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={(checked) => onUpdate({ enabled: checked })}
            disabled={loading}
            data-testid={`${config.key}-toggle`}
          />
        </div>
      </CardHeader>
      
      {isEnabled && (
        <CardContent className="pt-0 space-y-4">
          {/* Delivery Channels */}
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">
              Delivery Channels
            </Label>
            <div className="grid grid-cols-3 gap-2">
              <ChannelToggle
                icon={Smartphone}
                label="Push"
                checked={channels.push}
                onChange={(checked) => onUpdate({ channels: { ...channels, push: checked } })}
                disabled={loading}
              />
              <ChannelToggle
                icon={Mail}
                label="Email"
                checked={channels.email}
                onChange={(checked) => onUpdate({ channels: { ...channels, email: checked } })}
                disabled={loading}
              />
              <ChannelToggle
                icon={Monitor}
                label="In-App"
                checked={channels.in_app}
                onChange={(checked) => onUpdate({ channels: { ...channels, in_app: checked } })}
                disabled={loading}
              />
            </div>
          </div>

          {/* Frequency */}
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">
              Frequency
            </Label>
            <Select value={frequency} onValueChange={(value) => onUpdate({ frequency: value })}>
              <SelectTrigger className="w-full" data-testid={`${config.key}-frequency`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex flex-col">
                      <span>{opt.label}</span>
                      <span className="text-xs text-gray-500">{opt.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default function NotificationPreferences() {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('alert_email');

  // Fetch preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!userEmail) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/notification-preferences/`, {
          params: { user_email: userEmail }
        });
        setPreferences(response.data);
      } catch (error) {
        console.error('Error fetching preferences:', error);
        toast.error('Failed to load preferences');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPreferences();
  }, [userEmail]);

  // Save preferences
  const savePreferences = async () => {
    if (!userEmail || !preferences) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/notification-preferences/`, preferences, {
        params: { user_email: userEmail }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success('Preferences saved!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const resetToDefaults = async () => {
    if (!userEmail) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/notification-preferences/reset`, null, {
        params: { user_email: userEmail }
      });
      setPreferences(response.data.preferences);
      toast.success('Preferences reset to defaults');
    } catch (error) {
      console.error('Error resetting preferences:', error);
      toast.error('Failed to reset preferences');
    } finally {
      setSaving(false);
    }
  };

  // Update a specific alert type
  const updateAlertType = (alertType, updates) => {
    setPreferences(prev => ({
      ...prev,
      [alertType]: {
        ...prev[alertType],
        ...updates
      }
    }));
  };

  // Update global settings
  const updateGlobalSetting = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!userEmail) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <Card className="text-center py-12">
            <CardContent>
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
              <h2 className="text-xl font-semibold mb-2">Sign In Required</h2>
              <p className="text-gray-600 mb-4">
                Please sign in to manage your notification preferences
              </p>
              <Button onClick={() => navigate('/login')}>
                Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-600" />
          <p className="text-gray-600">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6" data-testid="notification-preferences-page">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Bell className="w-6 h-6 text-blue-600" />
                Notification Preferences
              </h1>
              <p className="text-gray-600 text-sm">
                Customize how and when you receive notifications
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={resetToDefaults}
              disabled={saving}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            <Button 
              onClick={savePreferences}
              disabled={saving}
              className="min-w-[100px]"
              data-testid="save-preferences-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : saved ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saved ? 'Saved!' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Stay in control of your notifications</p>
            <p className="text-blue-600 mt-1">
              Choose which alerts you want to receive and how often. Instant notifications 
              keep you updated in real-time, while digests bundle updates for less interruption.
            </p>
          </div>
        </div>

        {/* Alert Types */}
        <div className="space-y-4 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Alert Types
          </h2>
          
          {ALERT_TYPES.map(config => (
            <AlertTypeCard
              key={config.key}
              config={config}
              settings={preferences?.[config.key]}
              onUpdate={(updates) => updateAlertType(config.key, updates)}
              loading={saving}
            />
          ))}
        </div>

        <Separator className="my-8" />

        {/* Global Settings */}
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Scheduling & Timing
          </h2>

          {/* Quiet Hours */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-indigo-50">
                    <Moon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Quiet Hours</CardTitle>
                    <CardDescription>
                      Pause push notifications during specific hours
                    </CardDescription>
                  </div>
                </div>
                <Switch
                  checked={preferences?.quiet_hours_enabled}
                  onCheckedChange={(checked) => updateGlobalSetting('quiet_hours_enabled', checked)}
                  disabled={saving}
                  data-testid="quiet-hours-toggle"
                />
              </div>
            </CardHeader>
            
            {preferences?.quiet_hours_enabled && (
              <CardContent className="pt-0">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500 mb-1 block">Start</Label>
                    <Input
                      type="time"
                      value={preferences?.quiet_hours_start || '22:00'}
                      onChange={(e) => updateGlobalSetting('quiet_hours_start', e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <span className="text-gray-400 mt-5">to</span>
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500 mb-1 block">End</Label>
                    <Input
                      type="time"
                      value={preferences?.quiet_hours_end || '08:00'}
                      onChange={(e) => updateGlobalSetting('quiet_hours_end', e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Push notifications will be held and delivered when quiet hours end. 
                  Email and in-app notifications are not affected.
                </p>
              </CardContent>
            )}
          </Card>

          {/* Digest Schedule */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-amber-50">
                  <Mail className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Digest Schedule</CardTitle>
                  <CardDescription>
                    When to send daily and weekly digest emails
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Weekly digest day</Label>
                  <Select 
                    value={preferences?.digest_day || 'monday'}
                    onValueChange={(value) => updateGlobalSetting('digest_day', value)}
                  >
                    <SelectTrigger data-testid="digest-day-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map(day => (
                        <SelectItem key={day.value} value={day.value}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Preferred time</Label>
                  <Input
                    type="time"
                    value={preferences?.digest_time || '09:00'}
                    onChange={(e) => updateGlobalSetting('digest_time', e.target.value)}
                    className="w-full"
                    data-testid="digest-time-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timezone */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-teal-50">
                  <Clock className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Timezone</CardTitle>
                  <CardDescription>
                    All scheduled notifications use this timezone
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Select 
                value={preferences?.timezone || 'Asia/Colombo'}
                onValueChange={(value) => updateGlobalSetting('timezone', value)}
              >
                <SelectTrigger data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Asia/Colombo">Asia/Colombo (Sri Lanka)</SelectItem>
                  <SelectItem value="Europe/London">Europe/London (UK)</SelectItem>
                  <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                  <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST)</SelectItem>
                  <SelectItem value="Asia/Dubai">Asia/Dubai (UAE)</SelectItem>
                  <SelectItem value="Asia/Singapore">Asia/Singapore</SelectItem>
                  <SelectItem value="Australia/Sydney">Australia/Sydney</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>

        {/* Footer Save Button */}
        <div className="mt-8 flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button 
            onClick={savePreferences}
            disabled={saving}
            className="min-w-[140px]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : saved ? (
              <CheckCircle className="w-4 h-4 mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saved ? 'Saved!' : 'Save Preferences'}
          </Button>
        </div>
      </div>
    </div>
  );
}
